// src/utils/rumblePayment.js
// Client side of cash Rumbles (evergreen 3.3): one module for the three
// targets (a personal stream, a squad stream, an event).
//
// A Rumble is a purchase from CritCash. The server (stripeDeposits.js, the
// flood callables under their old names) validates the target, reserves the
// payer's daily limit, charges, and credits the receivers. This module only
// speaks the wire contract:
//  - every call sends EXACTLY {targetType, targetId} (setup) or
//    {targetType, targetId, amountCents} (charge). No previewV, no legacy
//    eventId/collection keys: the server ignores anything else anyway.
//  - one-tap runs with a 130s client timeout (server timeoutSeconds 120), so
//    the server's own success or tips-pending reply always lands first.
//  - an outcome the client cannot know (SDK deadline, Cloud Run 504, dropped
//    connection) reads as tips-pending: the saved card MAY have been charged,
//    and the webhook backstop records it. Never open a checkout after that.
//
// Fan-facing surfaces stay money-free; amounts appear only in the payer's own
// picker. Wave tiers mirror functions/modules/rumbles.js RUMBLE_WAVES.

import { Platform } from 'react-native';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';

// Picker fallbacks until getFloodPaymentSetup answers (the server is the
// authority). $500 is the per-purchase maximum for one Rumble; the 24-hour
// limit is the server's, and its picker maximum already applies it.
export const RUMBLE_MIN_CENTS = 100;
export const RUMBLE_MAX_CENTS = 50000;

// Wave tiers by a single Rumble's amount, highest first. Mirror of the server
// ladder: change both together.
export const RUMBLE_WAVES = [
  { min: 10000, id: 'tsunami' },
  { min: 5000, id: 'surge' },
  { min: 1000, id: 'wave' },
  { min: 0, id: 'ripple' },
];

const WAVE_LABELS = { tsunami: 'Tsunami', surge: 'Surge', wave: 'Wave', ripple: 'Ripple' };
const WAVE_COLORS = { tsunami: '#FCD000', surge: '#00bfff', wave: '#4ade80', ripple: '#9a9a9a' };

export function rumbleWave(cents) {
  const n = Number(cents) || 0;
  const hit = RUMBLE_WAVES.find((w) => n >= w.min);
  return (hit || RUMBLE_WAVES[RUMBLE_WAVES.length - 1]).id;
}

export function waveLabel(id) {
  return WAVE_LABELS[String(id || '').toLowerCase()] || WAVE_LABELS.ripple;
}

export function waveColor(id) {
  return WAVE_COLORS[String(id || '').toLowerCase()] || WAVE_COLORS.ripple;
}

// Client copy for an unknown one-tap outcome (the server sends its own
// tips-pending sentence when it knows; this covers the transport failures).
const PENDING_ERROR = "tips-pending: We couldn't confirm your Rumble yet. Please wait a minute before sending another.";

function targetPayload(target) {
  const targetType = target && target.targetType;
  const targetId = target && target.targetId;
  if (!targetType || !targetId) throw new Error("Rumbles aren't open here right now.");
  return { targetType: String(targetType), targetId: String(targetId) };
}

function chargePayload(target, amountCents) {
  const { targetType, targetId } = targetPayload(target);
  return { targetType, targetId, amountCents: Math.round(Number(amountCents) || 0) };
}

// SDK deadline, Cloud Run's 504 (deadline-exceeded), or a dropped connection
// (code internal, message exactly 'internal'). Not the server's own uppercase
// 'INTERNAL', and not 'unavailable' (a definite pre-charge refusal).
function isUnknownOutcome(err) {
  const code = String(err?.code || '');
  const raw = String(err?.message || '');
  if (code === 'functions/deadline-exceeded' || code === 'deadline-exceeded') return true;
  return (code === 'functions/internal' || code === 'internal') && raw === 'internal';
}

/**
 * Setup for the picker. With a target the server runs the target validator
 * and adds {tipsEnabled, recipientCount, reason} (reason is a fan sentence,
 * or 'tips-limit' when today's limit leaves less than the minimum).
 * Resolves the raw setup object; rejects on a transport or server error.
 */
export async function getRumbleSetup(target) {
  const fn = httpsCallable(functions, 'getFloodPaymentSetup');
  const hasTarget = !!(target && target.targetType && target.targetId);
  const { data } = hasTarget ? await fn(targetPayload(target)) : await fn();
  return data || null;
}

/**
 * One-tap on the saved card. Resolves the server reply on success. Rejects
 * with the server error, or with a tips-pending error when the outcome is
 * unknown (keeps the original code on .code).
 */
export async function sendRumbleOneTap(target, amountCents) {
  const payload = chargePayload(target, amountCents);
  const fn = httpsCallable(functions, 'floodOneTap', { timeout: 130000 });
  try {
    const { data } = await fn(payload);
    return data || { success: true };
  } catch (err) {
    if (isUnknownOutcome(err)) {
      const pending = new Error(PENDING_ERROR);
      pending.code = err?.code;
      pending.cause = err;
      throw pending;
    }
    throw err;
  }
}

/**
 * Embedded checkout for a first-time payer (also saves the card so the next
 * Rumble is one-tap). Resolves {clientSecret, publishableKey, ...}.
 */
export async function createRumbleCheckout(target, amountCents) {
  const payload = chargePayload(target, amountCents);
  const fn = httpsCallable(functions, 'createFloodCheckout');
  const { data } = await fn(payload);
  if (!data?.clientSecret) throw new Error('Could not start checkout.');
  return { ...data, publishableKey: data.publishableKey || null };
}

const readCallback = (r) => (typeof r === 'function' ? r : (r && typeof r.current === 'function' ? r.current : null));
const resolveContainer = (c) => (c && typeof c === 'object' && 'current' in c ? c.current : c);

/**
 * Mounts a Stripe embedded checkout (the house loadStripe +
 * createEmbeddedCheckoutPage pattern) and returns destroy(). Call it from an
 * effect keyed on the clientSecret only and return destroy as the cleanup:
 * callbacks come in as refs and are read when they fire, so a parent
 * re-render never tears down the form while the payer is typing.
 *
 * container: a DOM element or a ref to one (read when the form is ready).
 * onCompleteRef: ref (or function) called once after payment; the instance
 *   is already destroyed by then.
 * onErrorRef (optional): ref (or function) called with the error if the form
 *   cannot open; without it the payer gets the house alert.
 */
export function mountEmbeddedCheckout(container, { clientSecret, publishableKey } = {}, onCompleteRef, onErrorRef) {
  let cancelled = false;
  let instance = null;
  const destroy = () => {
    cancelled = true;
    const current = instance;
    instance = null;
    if (current) { try { current.destroy(); } catch (_) { /* noop */ } }
  };

  const fail = (err) => {
    console.error('[rumblePayment] embedded checkout mount failed:', err);
    const onError = readCallback(onErrorRef);
    if (onError) { onError(err); return; }
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.alert('Could not open the payment form. Please try again.');
    }
  };

  if (Platform.OS !== 'web' || !clientSecret) return destroy;

  (async () => {
    try {
      if (typeof publishableKey !== 'string' || !publishableKey) {
        throw new Error('Stripe publishable key is not configured.');
      }
      const { loadStripe } = await import('@stripe/stripe-js');
      const stripe = await loadStripe(publishableKey);
      if (cancelled) return;
      if (!stripe) throw new Error('Stripe failed to load.');

      const created = await stripe.createEmbeddedCheckoutPage({
        clientSecret,
        onComplete: () => {
          // Paid. Tear down first, then hand off; the webhook records it.
          destroy();
          const onComplete = readCallback(onCompleteRef);
          if (onComplete) onComplete();
        },
      });

      if (cancelled) { try { created.destroy(); } catch (_) { /* noop */ } return; }
      instance = created;
      const el = resolveContainer(container);
      if (!el) throw new Error('Checkout container is not mounted.');
      created.mount(el);
    } catch (err) {
      if (cancelled) return;
      destroy();
      fail(err);
    }
  })();

  return destroy;
}

/**
 * After an embedded checkout completes on a session created with card checks
 * on (the create returned cardCheck: true), the payment is only authorized
 * until the server checks the card and captures or cancels it. Polls
 * getTipPurchaseOutcome every 2 s for up to 20 s (STEP3_5A_TIERS_DESIGN 3.7).
 * kind: 'rumble' | 'super_crit' | 'team_super_crit'. isAlive (optional)
 * stops the polling once the surface closed. Resolves {status, reason}:
 * status 'completed' | 'failed' | 'pending' (still pending at the deadline,
 * or every read failed); reason 'card_limit' | 'card_blocked' |
 * 'account_limit' | null. Never rejects.
 */
export async function pollTipPurchaseOutcome(kind, purchaseId, { isAlive, intervalMs = 2000, timeoutMs = 20000 } = {}) {
  const fn = httpsCallable(functions, 'getTipPurchaseOutcome');
  const until = Date.now() + timeoutMs;
  const alive = () => (typeof isAlive === 'function' ? isAlive() : true);
  for (;;) {
    try {
      const { data } = await fn({ kind, purchaseId: String(purchaseId || '') });
      const status = data?.status;
      if (status === 'completed' || status === 'failed') return { status, reason: data?.reason || null };
    } catch (err) {
      console.warn('[rumblePayment] purchase outcome read failed:', err?.code || err?.message);
    }
    if (!alive() || Date.now() + intervalMs > until) return { status: 'pending', reason: null };
    await new Promise((r) => setTimeout(r, intervalMs));
    if (!alive()) return { status: 'pending', reason: null };
  }
}

/**
 * How a caller should treat a Rumble error:
 *  - 'pending'     outcome unknown; the card may be charged. Never retry into
 *                  checkout; ask the payer to wait.
 *  - 'unavailable' the target is not open for Rumbles (tips-unavailable:).
 *  - 'card'        the saved card can't be used (card-limit:, card-blocked:;
 *                  'limits' mode with card checks on only). Nothing was
 *                  charged; the payer may use another card in checkout.
 *  - 'limit'       the 24-hour limit for Super Crits and Rumbles (tips-limit:).
 *  - 'checkout'    saved card needs SCA / none on file: open the embedded
 *                  checkout (authentication-required, no-saved-card).
 *  - 'error'       anything else.
 */
export function rumbleErrorKind(err) {
  const raw = String(err?.message || '');
  if (raw.includes('tips-pending') || isUnknownOutcome(err)) return 'pending';
  if (raw.includes('card-limit') || raw.includes('card-blocked')) return 'card';
  if (raw.includes('tips-limit')) return 'limit';
  if (raw.includes('tips-unavailable')) return 'unavailable';
  if (raw.includes('authentication-required') || raw.includes('no-saved-card')) return 'checkout';
  return 'error';
}

/**
 * The fan-facing sentence for a Rumble error: the server's sentence after any
 * leading lowercase code token (tips-limit:, account-under-review:, ...), else
 * the fallback (bare codes such as 'internal' or 'no-saved-card' never reach
 * the payer). rumbleErrorKind still classifies on the raw tips-* tokens.
 */
export function rumbleErrorMessage(err, fallback = 'Could not send the Rumble. Please try again.') {
  const raw = String(err?.message || '').trim();
  const stripped = raw.replace(/^[a-z][a-z-]*:\s*/, '').trim();
  const kind = rumbleErrorKind(err);
  if (kind === 'pending') {
    return raw.includes('tips-pending') && stripped ? stripped : PENDING_ERROR.replace(/^tips-pending:\s*/, '');
  }
  if (kind === 'checkout') return fallback;
  return /\s/.test(stripped) ? stripped : fallback;
}
